import { useCallback, useMemo } from 'react';

import { t } from 'i18next';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import DiaryDetailView from './components/DiaryDetailView';
import useArtistQuery from '../../components/api/queries/useArtistQuery';
import useUserQuery from '../../components/api/queries/useUserQuery';
import { useGetUserBinder } from '../../domains/userBinders/ui/hooks/useGetUserBinder';
import { useCreateShortenLink } from '../../domains/shortenLinks/ui/hooks/useCreateShortenLink';
import copyTextToClipboard from '@pocapan/shared/utils/writeTextToClipboard';
import { useSuccessPhotoCardToast } from '../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import i18n from '../../i18n/i18n';
import { getArtistId, getUserId } from '@pocapan/shared/utils/etcUtils';
import { getDiaryCover, loadDiaryCoverMap } from '@pocapan/shared/utils/photoCardDiaryCoverStorage';
import { queryGetNoCache } from '@pocapan/shared/utils/queryClient';

const normalizeTranslationLocale = (language) => {
  if (!language) return 'en';
  if (language === 'zh-TW' || language === 'zh-HK' || language.startsWith('zh-TW')) return 'zh-TW';
  if (language === 'zh-CN' || language.startsWith('zh-CN')) return 'zh-CN';
  if (language.startsWith('en')) return 'en';
  if (language.startsWith('ko')) return 'ko';
  if (language.startsWith('jp') || language.startsWith('ja')) return 'jp';
  return language;
};

const getTranslatedValue = (translations, language, fallback = '') => {
  if (!translations) return fallback;
  const locale = normalizeTranslationLocale(language);
  return translations[locale] || fallback;
};

export default function MyPhotoCardDiaryDetailPage() {
  const { user_id: userId, member_id: memberId } = useParams();
  const navigate = useNavigate();
  const artistId = getArtistId();
  const boardPath = `/users/${userId}/binder`;
  const artist = useArtistQuery(artistId);
  const user = useUserQuery();
  const currentUserId = user?.userId || getUserId() || '';

  const { mutateAsync: createLink, isLoading: isCreatingLink } = useCreateShortenLink();
  const showToast = useSuccessPhotoCardToast();

  const members = artist?.members || [];
  const likedMembers = members.filter((member) => member.isLiked);
  const memberPool = likedMembers.length > 0 ? likedMembers : members;
  const currentMember =
    memberPool.find((member) => member.memberId === memberId) ||
    members.find((member) => member.memberId === memberId);
  const currentMemberIndex = Math.max(
    memberPool.findIndex((member) => member.memberId === memberId),
    0
  );
  const { data: userBinderMap = {} } = useGetUserBinder(currentUserId, userId, artistId, {
    enabled: !!currentUserId
  });
  const userBinder = memberId ? userBinderMap[memberId] : null;

  const currentCover = getDiaryCover({
    coverMap: loadDiaryCoverMap({ userId, artistId }),
    member: currentMember,
    memberIndex: currentMemberIndex
  });
  const coverSubtitle = artist?.name || artist?.shortName || undefined;
  const currentLanguage = i18n.resolvedLanguage || i18n.language || 'en';
  const localizedMemberName = getTranslatedValue(
    currentMember?.translations?.name,
    currentLanguage,
    currentMember?.name || ''
  );
  const localizedArtistShortName = getTranslatedValue(
    artist?.translations?.shortName,
    currentLanguage,
    artist?.name || artist?.shortName || ''
  );
  const closingEngraving = useMemo(() => {
    const resolvedTitle =
      currentCover.title === currentMember?.name
        ? localizedMemberName
        : currentCover.title || localizedMemberName;

    return {
      eyebrow: localizedArtistShortName || coverSubtitle || '',
      title: resolvedTitle || ''
    };
  }, [
    coverSubtitle,
    currentCover.title,
    currentMember?.name,
    localizedArtistShortName,
    localizedMemberName
  ]);
  const collectorName =
    currentUserId === userId
      ? user?.baseUserProfile?.nickname || user?.nickname || undefined
      : undefined;

  const { data: memberPhotoCardData, isLoading: isMemberPhotoCardLoading } = useQuery(
    ['diaryPhotoCards', artistId, memberId],
    () => queryGetNoCache(`/artists/${artistId}/members/${memberId}/photo_cards`),
    {
      enabled: !!artistId && !!memberId,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always'
    }
  );

  const collectedPhotoCards = useMemo(() => {
    return (
      memberPhotoCardData?.events?.flatMap((eventGroup) => {
        return (eventGroup.photoCards || [])
          .filter((photoCard) => photoCard.collectedCount > 0)
          .map((photoCard) => ({ ...photoCard, eventName: eventGroup.event?.name }));
      }) || []
    );
  }, [memberPhotoCardData]);

  const handleShare = async () => {
    try {
      const { key } = await createLink({ artistId, memberId });
      const url = `${window.location.origin}/binder-share.html?key=${key}`;
      await copyTextToClipboard(url);
      showToast(
        'bottom',
        t('myPhotoCardDiary.linkCopied', { defaultValue: '링크가 복사되었어요.' })
      );
    } catch {
      // 공유/클립보드 실패는 조용히 무시
    }
  };

  const shareButton = (
    <button
      type="button"
      className="diary-share-button"
      aria-label={t('myPhotoCardDiary.share', { defaultValue: '공유하기' })}
      disabled={isCreatingLink}
      onClick={handleShare}
    >
      <span aria-hidden="true" className="diary-share-button-icon" />
    </button>
  );
  const handleBackToBoard = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(boardPath, { replace: true });
  }, [boardPath, navigate]);

  return (
    <DiaryDetailView
      collectedPhotoCards={collectedPhotoCards}
      cover={currentCover}
      coverImageUrl={userBinder?.imageUrl || undefined}
      memberName={localizedMemberName || currentMember?.name}
      coverSubtitle={coverSubtitle}
      collectorName={collectorName}
      closingEngraving={closingEngraving}
      isLoading={isMemberPhotoCardLoading}
      onBack={handleBackToBoard}
      shareButton={isMemberPhotoCardLoading ? null : shareButton}
    />
  );
}
